import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/auth/AuthContext";
import { countUnsynced } from "@/offline/syncEngine";

interface SignOutButtonProps {
  className?: string;
  title?: string;
  "data-testid"?: string;
  children: React.ReactNode;
}

/**
 * Signs the user out, first warning them if climbs are still waiting to sync,
 * since logging out deletes them from this device.
 */
export function SignOutButton({
  children,
  ...buttonProps
}: SignOutButtonProps) {
  const { logout } = useAuth();
  const [unsynced, setUnsynced] = useState(0);
  const checking = useRef(false);

  useEffect(() => {
    if (unsynced === 0) return;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUnsynced(0);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [unsynced]);

  const requestSignOut = async () => {
    if (checking.current) return; // a double tap would sign out twice
    checking.current = true;
    // If the queue can't be read, don't trap the user signed in: sign out as before.
    const count = await countUnsynced().catch((err) => {
      Sentry.captureException(err);
      return 0;
    });
    // Stay locked once signing out: the button lingers until the redirect.
    if (count === 0) logout();
    else {
      checking.current = false;
      setUnsynced(count);
    }
  };

  return (
    <>
      <button type="button" onClick={requestSignOut} {...buttonProps}>
        {children}
      </button>
      {unsynced > 0 &&
        // Portaled so the overlay covers the page even from inside the side nav.
        createPortal(
          <>
            <div
              className="fixed inset-0 z-50 bg-black/80"
              onClick={() => setUnsynced(0)}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="sign-out-title"
                aria-describedby="sign-out-description"
                className="modal-panel w-full max-w-sm p-6 flex flex-col gap-4"
              >
                <h2
                  id="sign-out-title"
                  className="font-display text-2xl tracking-wide"
                >
                  SIGN OUT?
                </h2>
                <p id="sign-out-description" className="text-sm text-text-muted leading-relaxed">
                  {unsynced === 1
                    ? "1 item hasn't"
                    : `${unsynced} items haven't`}{" "}
                  synced yet. Signing out deletes{" "}
                  {unsynced === 1 ? "it" : "them"} from this device.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    // Cancel is the safe default for a destructive confirm.
                    autoFocus
                    onClick={() => setUnsynced(0)}
                    className="flex-1 btn-ghost text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUnsynced(0);
                      logout();
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl py-3 text-sm font-semibold transition-colors"
                  >
                    Sign out anyway
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
